import { onboardingStore } from '@/fsd/entities/onboarding/model/store/onboardingStore';
import { routes } from '@/fsd/shared/config/routes/routesConfig';
import { Button } from '@/fsd/shared/ui/button';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import {
  ACTIONS,
  EVENTS,
  STATUS,
  TooltipRenderProps,
  useJoyride,
} from 'react-joyride';
import { useNavigate } from 'react-router-dom';
import styles from './onboardingTooltip.module.scss';
import CloseIcon from '@/fsd/shared/ui/icons/Close';
import { isPrimaryButtonEnabled } from '@/fsd/entities/onboarding/lib/isPrimaryButtonEnabled';
import Modal from '@fsd/shared/ui/Modal/Modal';

interface OnboardingTooltipProps {
  onEvent?: (data: any) => void;
}

const CustomTooltip = ({
  backProps,
  primaryProps,
  index,
  size,
  step,
  tooltipProps,
}: TooltipRenderProps) => {
  const navigate = useNavigate();
  const showActions = !(
    step.buttons !== undefined && step.buttons.length === 0
  );
  const [showConfirm, setShowConfirm] = useState(false);

  const isDataLoaded = isPrimaryButtonEnabled(step);

  return (
    <>
      <div
        {...tooltipProps}
        className={styles.tooltip}
        style={{ width: step.styles.tooltip.width }}
      >
        <div className={styles.header}>
          {step.title && <h3 className={styles.title}>{step.title}</h3>}
          <button
            className={styles.closeButton}
            onClick={() => {
              setShowConfirm(true);
            }}
          >
            <CloseIcon />
          </button>
        </div>
        <div className={styles.content}>{step.content}</div>
        <div className={styles.footer}>
          <div className={styles.stepCounter}>
            Шаг {index + 1} из {size}
          </div>
          {showActions && (
            <div className={styles.actions}>
              {step.buttons.find((item) => item === 'back') &&
                (step.title === 'Первый этап завершён!' ||
                step.title === 'Второй этап завершён!' ||
                step.title === 'Вы собрали полный P&L!' ? (
                  <Button
                    className={styles.action}
                    style={{ backgroundColor: 'white' }}
                    variant="outline"
                    size="m"
                    title={'В P&L'}
                    onClick={() => {
                      navigate(routes.pnl.path);
                      onboardingStore.stopTour();
                    }}
                  >
                    В P&L
                  </Button>
                ) : step.title === 'Вы собрали полный ДДС!' ? (
                  <Button
                    className={styles.action}
                    style={{ backgroundColor: 'white' }}
                    variant="outline"
                    size="m"
                    title={'В ДДС'}
                    onClick={() => {
                      navigate(routes.dds.path);
                      onboardingStore.stopTour();
                    }}
                  >
                    В ДДС
                  </Button>
                ) : index > 0 ? (
                  <Button
                    {...backProps}
                    className={styles.action}
                    style={{ backgroundColor: 'white' }}
                    variant="outline"
                    size="m"
                    title={'Назад'}
                  >
                    Назад
                  </Button>
                ) : (
                  <></>
                ))}
              {step.buttons.find((item) => item === 'primary') &&
                isDataLoaded && (
                  <Button
                    {...primaryProps}
                    className={styles.action}
                    variant="primary"
                    size="m"
                    title={index === size - 1 ? 'Завершить' : 'Далее'}
                  >
                    {index === size - 1 ? 'Завершить' : 'Далее'}
                  </Button>
                )}
            </div>
          )}
        </div>
      </div>
      {showConfirm && (
        <Modal
          z={101}
          showModal={showConfirm}
          setShowModal={(show) => {
            setShowConfirm(show);
          }}
          className={styles.confirmModal}
        >
          <p>
            После завершения, вам придется начать настройку с начала текущего
            раздела. Вы уверены, что хотите завершить онбординг?
          </p>
          <div className={styles.actions}>
            <Button
              className={styles.action}
              style={{ backgroundColor: 'white' }}
              variant="outline"
              size="m"
              title={'Назад'}
              onClick={() => {
                setShowConfirm(false);
              }}
            >
              Назад
            </Button>
            <Button
              className={styles.action}
              variant="primary"
              size="m"
              title={'Завершить'}
              onClick={() => {
                onboardingStore.stopTour();
                setShowConfirm(false);
              }}
            >
              Завершить
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
};

export const OnboardingTooltip = observer(
  ({ onEvent }: OnboardingTooltipProps) => {
    const navigate = useNavigate();

    useEffect(() => {
      if (
        !onboardingStore.run ||
        onboardingStore.activeTourSteps.length === 0
      ) {
        return;
      }

      let isActive = true;

      const ensureReady = async () => {
        const step = onboardingStore.getCurrentStep();
        if (!step) {
          return;
        }

        const prevStep = onboardingStore.getPrevStep();
        if (
          prevStep &&
          prevStep.data?.next &&
          window.location.pathname !== prevStep.data.next
        ) {
          navigate(prevStep.data.next);
        }

        const ready = await onboardingStore.ensureCurrentStepTargetReady();
        if (!isActive || !ready) {
          return;
        }
      };

      void ensureReady();

      return () => {
        isActive = false;
      };
    }, [
      onboardingStore.run,
      onboardingStore.currentStepIndex,
      onboardingStore.activeTourSteps.length,
    ]);

    const { Tour } = useJoyride({
      continuous: true,
      run: onboardingStore.run,
      steps: toJS(onboardingStore.activeTourSteps),
      stepIndex: onboardingStore.currentStepIndex,
      onEvent: (data) => {
        const { action, index, step, type, status } = data;
        if (
          action === ACTIONS.RESET ||
          action === ACTIONS.SKIP ||
          status === STATUS.SKIPPED ||
          status === STATUS.FINISHED
        ) {
          return;
        }

        if (action === ACTIONS.CLOSE || action === ACTIONS.STOP) {
          return;
        }

        if (type === EVENTS.TARGET_NOT_FOUND) {
          onboardingStore.setStepIndex(onboardingStore.currentStepIndex + 1);
          return;
        }

        if (type === EVENTS.STEP_AFTER) {
          if (!onboardingStore.run) {
            return;
          }

          if (action === ACTIONS.NEXT) {
            const nextIndex = (index ?? onboardingStore.currentStepIndex) + 1;
            onboardingStore.setStepIndex(nextIndex);

            if (step?.data?.next) {
              navigate(step.data.next);
            }
          } else if (action === ACTIONS.PREV) {
            const prevIndex = (index ?? onboardingStore.currentStepIndex) - 1;
            onboardingStore.setStepIndex(prevIndex);

            if (step?.data?.previous) {
              navigate(step.data.previous);
            }
          }
        }

        onEvent?.(data);
      },
      tooltipComponent: CustomTooltip,
      options: {
        primaryColor: '#1976d2',
        arrowColor: '#fff',
        overlayColor: 'rgba(0, 0, 0, 0.5)',
      },
    });

    return <>{Tour}</>;
  }
);
